/**
 * RC1-1 (Spec №16 review-cycle-1) — the ONE agent identity-colour derivation.
 *
 * The problem this file closes: an agent's identity colour used to be derived
 * per-surface, from two incompatible keys (`path` in the rail/cockpit, `name` in
 * activity) plus a third state (flat purple in the workflow surfaces, no colour
 * at all). The same agent — "MA · Matter Assistant" — rendered ORANGE in the nav
 * rail and PURPLE on a workflow row. This module is now the single source of the
 * hash→persona mapping; every surface routes through it, so one agent is one
 * colour everywhere, in both themes.
 *
 * The derivation is deterministic from the agent's STABLE node id (never from
 * display context like path or name):
 *   precedence = stored `persona_color` (only when a full node is available)
 *              → else a hash of the agent's node id (the `ln_…` id).
 * The node id is the one key every surface shares — the rail/cockpit carry a full
 * node, the workflow surfaces carry only the assignee's node id (`assigneeCode`),
 * activity carries `agent.id` — so hashing the id makes them all agree.
 *
 * Rendering: agent avatars go through <Avatar>/<NodeAvatar> in primitives.tsx.
 * Where a surface builds a raw avatar span (the perf-sensitive workflow rows) it
 * feeds its background from `personaFill(agentPersonaFromId(id))` — same id, same
 * colour, no second palette.
 *
 * ─── AUDIT: every agent-identity surface routes through here ──────────────────
 *   Surface                         Component / call                         Key
 *   Staff / Workforce rail          ScopeRail → NodeAvatar, agentPersona      id
 *   Desk / cockpit header           CockpitHeader → NodeAvatar, agentPersona  id
 *   Module section rows             ModuleSection → ScopeRow, agentPersona    id
 *   Agents panel / active work      AgentsPanel/ActiveWorkPanel → Avatar      id
 *   Workforce cards / team / pulse  AgentCard/…/WorkforcePulseCard → Avatar   id
 *   Agent profile (header/identity) AgentProfile* → Avatar, agentPersona      id
 *   Member / resource pickers       MemberPicker/ResourcePicker → Avatar      id
 *   Task rows (My tasks / Home)     MyTasks/WorkspaceHome/ActiveTasks → Avatar id
 *   Change-owner / set-task modals  ChangeOwnerModal/SetTaskModal → Avatar    id
 *   Activity feed (recent/buckets)  RecentActivity/ActivityBucket → Avatar    id
 *   Workflow rows (WorkRow)         WorkRow → .avatar--agent, agentPersonaFromId id
 *   Workflow step-card assignee     StepCardDrawer → .avatar--agent           id
 *   Workflow full spine + picker    WorkflowDriveLens → .wfv-avm.is-agent     id
 *   Hire suggestion (pre-hire)      HireSuggestionCard → personaFill          name*
 *
 * *HireSuggestionCard shows a not-yet-hired suggestion that has no node id — the
 *  only surface without a stable id — so it hashes the suggested name. There is no
 *  rail avatar for it to match; it still uses this one derivation (no local hash).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import type { Persona } from "../components/primitives";
import { PERSONA_FILL } from "../components/primitives";

/**
 * The identity palette, in a fixed order so the hash→persona mapping is stable.
 * (This is the keeper order from the old CockpitHeader derivation — 9 slots,
 * including gray.) Kept private: the grep-gate forbids indexing an agent palette
 * by a hash anywhere but this file, so nothing outside can fork the mapping.
 */
const PERSONA_ORDER: Persona[] = ["purple", "teal", "coral", "pink", "blue", "green", "amber", "red", "gray"];

/** djb2-ish string hash → a stable palette slot. Identical maths to the five
 *  derivations this file replaces — only the KEY it is fed changed (id, not path/
 *  name), so every surface that shares an id now lands on the same colour. */
function hashToPersona(key: string): Persona {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
  }
  return PERSONA_ORDER[Math.abs(hash) % PERSONA_ORDER.length] ?? "gray";
}

/** True when a string is one of our persona names (a valid stored persona_color). */
function isPersona(v: string | null | undefined): v is Persona {
  return !!v && (PERSONA_ORDER as string[]).includes(v);
}

/**
 * Identity persona from a bare agent id / handle — for the workflow surfaces that
 * only carry the assignee's node id (`assigneeCode`), and any other id-only spot.
 */
export function agentPersonaFromId(id: string | null | undefined): Persona {
  if (!id) return "gray";
  return hashToPersona(id);
}

/** The minimal node shape the derivation needs. Any LibraryNode / detail (or a
 *  bare `{ id, path }` from a task roll-up) satisfies it. `agent_config` is left
 *  `unknown` so both the typed `AgentConfig` and a `Record<string, unknown>` cast
 *  pass without a wrapper — the stored colour is read defensively. */
export interface PersonaNode {
  id?: string | null;
  path?: string | null;
  agent_config?: unknown;
}

/**
 * Identity persona from a full node: an explicitly stored `persona_color` wins,
 * otherwise the hash of the node's stable id (falling back to `path` only if a
 * caller has no id to give — real nodes always do).
 */
export function agentPersona(node: PersonaNode | null | undefined): Persona {
  if (!node) return "gray";
  const stored = (node.agent_config as { persona_color?: unknown } | null | undefined)?.persona_color;
  if (typeof stored === "string" && isPersona(stored)) return stored;
  const key = node.id || node.path || "";
  return key ? hashToPersona(key) : "gray";
}

/** The avatar background (a CSS variable) for a persona — the one place raw avatar
 *  spans read their fill so they match a <Avatar> for the same agent. */
export function personaFill(persona: Persona): string {
  return PERSONA_FILL[persona];
}
